/**
 * App root — Abyss shell + route table (monitor-only build, no auth).
 *
 * Routes:
 *   /              Monitor         — probe trend charts (home page)
 *   /apex          Apex live       — full Apex telemetry page (read-only)
 *   /monitor       Monitor         — legacy alias for / (old bookmarks)
 *   /stock-solutions  Stock Solutions — DIY dosing calculator
 *   /test-kits     Test Kits       — aquarium test kit assistant
 *   /coral-placer  Coral Placer    — Reef Coral Placer
 *   /tasks         Tasks           — guided task catalog
 *   /totm          TOTM Archive    — embedded Reefkeeping TOTM archive (Pi-hosted)
 *   /logs/alarms   Alarms          — alarm history (placeholder)
 *   /logs/parameters  Parameter Logs — manual reading log (IndexedDB)
 *   /settings      Settings        — Apex connection, storage, backup
 *   /dev           Dev panel       — gated by VITE_REEFAPP_DEV
 *
 * Layout
 * ------
 * Every route except /kiosk renders inside `<AppShell>` (sidebar + mobile
 * chrome). There is no login — the Pi API is reachable only over the
 * owner's Tailscale network, which is the access-control layer.
 */
import { type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';

import { AppShell } from './components/AppShell';
import { ToastProvider } from './components/ui/Toast';
import { ApexPage } from './pages/ApexPage';
import { CalendarPage } from './pages/CalendarPage';
import { CoralPlacerPage } from './pages/CoralPlacerPage';
import { DevPage } from './pages/Dev';
import { DosingSchedulePage } from './pages/DosingSchedulePage';
import { LogsAlarmsPage } from './pages/LogsAlarmsPage';
import { MonitorPage } from './pages/MonitorPage';
import { NotesPage } from './pages/NotesPage';
import { ParameterLogsPage } from './pages/ParameterLogsPage';
import { PoolPage } from './pages/PoolPage';
import { SettingsPage } from './pages/SettingsPage';
import { StockSolutionsPage } from './pages/StockSolutionsPage';
import { TasksPage } from './pages/TasksPage';
import { TestKitsPage } from './pages/TestKitsPage';
import { TotmArchivePage } from './pages/TotmArchivePage';
import { Kiosk } from './pages/kiosk/Kiosk';
import { NotFound } from './pages/NotFound';

function Shell({ children }: { children: ReactNode }) {
  return <AppShell>{children}</AppShell>;
}

export function App() {
  return (
    <ToastProvider>
      <Routes>
        <Route path="/kiosk" element={<Kiosk />} />
        <Route path="/" element={<Shell><MonitorPage /></Shell>} />
        <Route path="/apex" element={<Shell><ApexPage /></Shell>} />
        {/* Legacy alias — Monitor moved to "/" but old bookmarks keep working. */}
        <Route path="/monitor" element={<Shell><MonitorPage /></Shell>} />
        <Route path="/stock-solutions" element={<Shell><StockSolutionsPage /></Shell>} />
        <Route path="/test-kits" element={<Shell><TestKitsPage /></Shell>} />
        <Route path="/coral-placer" element={<Shell><CoralPlacerPage /></Shell>} />
        <Route path="/tasks" element={<Shell><TasksPage /></Shell>} />
        <Route path="/calendar" element={<Shell><CalendarPage /></Shell>} />
        <Route path="/notes" element={<Shell><NotesPage /></Shell>} />
        <Route path="/dosing-schedule" element={<Shell><DosingSchedulePage /></Shell>} />
        <Route path="/logs/parameters" element={<Shell><ParameterLogsPage /></Shell>} />
        <Route path="/logs/alarms" element={<Shell><LogsAlarmsPage /></Shell>} />
        <Route path="/pool" element={<Shell><PoolPage /></Shell>} />
        <Route path="/totm" element={<Shell><TotmArchivePage /></Shell>} />
        <Route path="/settings" element={<Shell><SettingsPage /></Shell>} />
        <Route path="/dev" element={<Shell><DevPage /></Shell>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ToastProvider>
  );
}
